import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Typography, Box, FormControlLabel, Checkbox } from '@mui/material';
import * as taskTemplateStyle from 'mui-mobile-components/kart/fieldmap/tasktemplates/TaskTemplateStyle';
import MuiTextField from 'components/mui3-forms/MuiTextField';
import MuiSelectMobileDropdown from 'components/mui3-forms/MuiSelectMobileDropdown';
import BottomSheetNestedDropdown from "../BottomSheetNestedDropdown";
import { pesticideUnitOptions } from "utils/DynamicFields";
import MuiMultiSelectBottomSheet from '../MuiMultiSelectBottomSheet';
import MuiTextFieldArea from 'components/mui3-forms/MuiTextFieldArea';
import _ from 'lodash';

const AddTaskWaterJournalTemplate1 = forwardRef((props, ref) => {
  const { intl, fieldMasterData, activitiesCommon, onFieldChange, formData, handleNumericValidation, formErrors, waterJournal } = props;
  let pesticideDropdowns = _.filter(activitiesCommon, ['activityId', 'act19'])[0].dropdowns[2].elements;
  const [selectedPesticideTypes, setSelectedPesticideTypes] = useState([]);
  const [isChecked, setIsChecked] = useState(false);
  const [distanceChecked, setDistanceChecked] = useState(false);
  const [vegetationChecked, setVegetationChecked] = useState(false);
  const selectedColor = '#00696D';

  useImperativeHandle(ref, () => ({
    isValid: () => {
      const waterJournalAttributesPresent = (
        waterJournal?.nozzleName &&
        waterJournal?.nozzleFactory &&
        waterJournal?.nozzleHeight &&
        waterJournal?.pressure &&
        waterJournal?.driftReduction &&
        waterJournal?.maxDrivingSpeed &&
        waterJournal?.distanceToWaterBody &&
        waterJournal?.distanceToWaterWell &&
        (isChecked ? (
          waterJournal?.bufferZonePlacement &&
          waterJournal?.locationAndDistance
        ) : true) &&
        (distanceChecked ? (
          waterJournal?.distanceOfSurfaceWater
        ) : true) &&
        (vegetationChecked ? (
          waterJournal?.distanceOfVegitagedBufferZone
        ) : true) &&
        (waterJournal?.lessThan2PCSlope)
      );

      const isWaterJournalValid = waterJournalAttributesPresent;
      // console.log('isWaterJournalValid: ', isWaterJournalValid);

      let isPesticideValid = false;

      if (formData.pesticideType?.length > 0) {
        setSelectedPesticideTypes(formData.pesticideType);

        isPesticideValid = formData.pesticideType.every((pesticideName, index) => {
          const amountKey = `pesticideType.${index}.amount`;
          const unitKey = `pesticideType.${index}.unit`;
          return formData[amountKey] && formData[unitKey];
        });
        // eslint-disable-next-line
        formData.pesticideType.every((pesticideName, index) => {
          const amountKey = `pesticideType.${index}.amount`;
          const unitKey = `pesticideType.${index}.unit`;
          formData[amountKey] = formData[`pesticideType.${index}.amount`];
          formData[unitKey] = formData[`pesticideType.${index}.unit`];
        });
      }

      // console.log("formData.pesticide: ", formData);
      // console.log("pestData: ", formData.taskActivity?.pestData);
      const isPestSubtypeValid = formData?.taskActivity?.pestData;
      // console.log('isPestSubtypeValid: ', isPestSubtypeValid);

      const requiredFields = [
        formData.taskActivity?.crop,
        isWaterJournalValid,
        isPesticideValid,
        isPestSubtypeValid,
        formData.reason,
        formData.effectApplication,
      ];

      const hasFormErrors = formErrors && Object.values(formErrors).some(error => error);
      // console.log('requiredFields: ', requiredFields);
      // const isValid = requiredFields.every(Boolean);
      // console.log('isValid', isValid);

      return requiredFields.every(Boolean) && !hasFormErrors;
    },
  }));

  const handleChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleDistanceChange = (event) => {
    setDistanceChecked(event.target.checked);
  };

  const handleVegetationChange = (event) => {
    setVegetationChecked(event.target.checked);
  };

  const handlePesticideTypeChange = (event) => {
    setSelectedPesticideTypes(event.target.value);
    if (onFieldChange) {
      onFieldChange('pesticideType', event.target.value);
    }
  };

  // Effect of pesticide application
  const getEffectOfApplicationValues = () => {
    const values = ['God', 'Ok', 'Dårlig'];
    return values.map(value => ({
      value,
      label: value,
    }));
  };

  // Crop List
  const getDropDownFieldsStatuses = dropdownList => {
    return dropdownList?.map(item => ({
      value: item.name,
      label: item.name
    }));
  };

  // lessThan2PCSlope
  const lessThanTwoPercentSlope = () => {
    return [
      { value: 'true', label: 'Ja' },
      { value: 'false', label: 'Nei' }
    ];
  };

  // Pesticide type options
  const pesticideTypeOptions = (activitiesCommon) => {
    const pesticideActivity = activitiesCommon?.find(
      (activity) => activity.activityId === 'act19'
    );
    const pesticideDropdown = pesticideActivity?.dropdowns?.find(
      (dropdown) => dropdown.element === 'pesticide'
    );
    return pesticideDropdown?.leaves || [];
  };

  // console.log("pestInfo: ", formData.pestInfo);
  let pestDataValue = {
    element: formData?.taskActivity?.pestData,
    leaves: formData?.taskActivity['pestSubList'] || null
  }
  // console.log('pestDataValue: ', pestDataValue);

  return (
    <Box sx={{
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#A8A8A8',
        borderRadius: '4px',
        transition: 'background-color 0.3s ease',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#B8B8B8',
      }
    }}
    >
      <Box sx={{ display: "flex", paddingTop: '56px', alignItems: "center" }}>
        {/* Nozzle name */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "NOZZLE_NAME_WATER_JOURNAL" })}
          defaultValue={formData.waterJournal.nozzleName ?? ''}
          fieldName='nozzleName'
          onChange={onFieldChange}
          required={true}
          errorHelperText={formErrors['nozzleName']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Nozzle factory */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'NOZZLE_FACTORY_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.nozzleFactory ?? ''}
          fieldName='nozzleFactory'
          onChange={onFieldChange}
          required={true}
          errorHelperText={formErrors['nozzleFactory']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Nozzle height */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "NOZZLE_HEIGHT_WATER_JOURNAL" })}
          defaultValue={formData.waterJournal.nozzleHeight ?? ''}
          fieldName='nozzleHeight'
          numberKeypad={true}
          onChange={handleNumericValidation}
          required={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_CM' })}
          errorHelperText={formErrors['nozzleHeight']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Pressure */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'PRESSURE_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.pressure ?? ''}
          fieldName='pressure'
          required={true}
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_BAR' })}
          errorHelperText={formErrors['pressure']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Drift reduction */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DRIFT_REDUCTION' })}
          defaultValue={formData.waterJournal.driftReduction ?? ''}
          fieldName='driftReduction'
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_PERCENTAGE' })}
          errorHelperText={formErrors['driftReduction']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Max Driving speed */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_JOURNAL_DRIVING_SPEED' })}
          defaultValue={formData.waterJournal.maxDrivingSpeed ?? ''}
          fieldName='maxDrivingSpeed'
          numberKeypad={true}
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_KM' })}
          autoFocus={false}
          errorHelperText={formErrors['maxDrivingSpeed']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'FIELD_DETAILS' })}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '24px' }}>
        {/* Crop */}
        <MuiSelectMobileDropdown
          defaultLabel={`${intl.formatMessage({ id: 'CT_CR' })}*`}
          defaultValue={formData.taskActivity?.crop || ''}
          fieldName='crop'
          options={getDropDownFieldsStatuses(fieldMasterData.cropList)}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          bottomSheetHeight="41vh"
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* less than 2 percent slope */}
        <MuiSelectMobileDropdown
          defaultLabel={`${intl.formatMessage({ id: 'PC_SLOPE_WATER_JOURNAL' })}*`}
          defaultValue={formData.waterJournal.lessThan2PCSlope ?? ''}
          fieldName='lessThan2PCSlope'
          options={lessThanTwoPercentSlope()}
          required={true}
          onChange={onFieldChange}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* distance to water body */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "WATER_JOURNAL_DISTANCE_TO_WATER_BODY" })}
          defaultValue={formData.waterJournal.distanceToWaterBody ?? ''}
          fieldName='distanceToWaterBody'
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          errorHelperText={formErrors['distanceToWaterBody']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* distance to water well */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'WATER_WELL_DISTANCE_WATER_JOURNAL' })}
          defaultValue={formData.waterJournal.distanceToWaterWell ?? ''}
          fieldName='distanceToWaterWell'
          onChange={handleNumericValidation}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          autoFocus={false}
          numberKeypad={true}
          errorHelperText={formErrors['distanceToWaterWell']}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop:'32px' }}>
        {/* vegetated buffer exists */}
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                handleChange(e); // If you need to update `isChecked`
                onFieldChange('vegetatedBufferExists', checked); // Call your field change handler
              }}
              sx={{
                color: isChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={`${intl.formatMessage({ id: 'ESTABLISHED_BUFFER_ZONE' })}?`}
          sx={taskTemplateStyle.modalDialogSubTitle}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '24px' }}>
        {/* New field - 26 bufferzonePlacement*/}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: "LOCATION_ON_BUFFER_ZONE" })}
          defaultValue={formData.waterJournal.bufferZonePlacement ?? ''}
          fieldName='bufferZonePlacement'
          onChange={onFieldChange}
          numberKeypad={true}
          autoFocus={false}
          required={isChecked ? true : false}
          errorHelperText={formErrors['bufferZonePlacement']}
          disabled={!isChecked} // Disable based on isChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* locationAndDistance */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'LENGTH_BUFFER_ZONE' })}
          defaultValue={formData.waterJournal.locationAndDistance ?? ''}
          fieldName='locationAndDistance'
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          required={isChecked ? true : false}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          errorHelperText={formErrors['locationAndDistance']}
          disabled={!isChecked} // Disable based on isChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'PESTICIDE_INFO' })}
        </Typography>
      </Box>
      <Box sx={{ width: '100%', display: "flex", flexDirection: 'column', alignItems: "flex-start", paddingTop: '24px' }}>
        <MuiMultiSelectBottomSheet
          label={`${intl.formatMessage({ id: 'TYPE_OF_PESTICIDE' })}*`}
          value={selectedPesticideTypes}
          name='pesticideType.name'
          options={pesticideTypeOptions(activitiesCommon)}
          onChange={handlePesticideTypeChange}
          autoFocus={false}
          fullWidth={true}
          required={true}
          errorHelperText={formErrors['pesticideType.name']}
          bottomSheetHeight="60vh"
          customSx={{
            width: '100%',
            marginLeft: 0,
            '& .MuiFormControl-root': {
              width: '100% !important',
              marginLeft: 0,
            },
            '& .MuiOutlinedInput-root': {
              width: '100% !important',
            },
            '& .MuiSelect-select': {
              width: '100% !important',
            },
          }}
        />

        {selectedPesticideTypes.map((pesticideName, index) => {
          const fieldKey = `pesticideType[${pesticideName || 'default'}]`;
          return (
            <Box
              key={fieldKey}
              sx={{ display: 'flex', flexDirection: 'column', paddingTop: '24px', width: '100%' }}
            >
              <Typography sx={{ paddingBottom: '10px' }}>{pesticideName}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Pesticide Amount */}
                <MuiTextField
                  defaultLabel={intl.formatMessage({ id: 'AMOUNT_OF_PESTICIDE' })}
                  defaultValue={formData[`pesticideType.${index}.amount`] || ''}
                  fieldName={`pesticideType.${index}.amount`}
                  numberKeypad={true}
                  onChange={handleNumericValidation}
                  autoFocus={false}
                  required={true}
                  errorHelperText={formErrors[`pesticideType.${index}.amount`]}
                  fullWidth
                  activityId="act19"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {/* Pesticide Unit */}
                <MuiSelectMobileDropdown
                  defaultLabel={`${intl.formatMessage({ id: 'CT_UNIT' })}*`}
                  defaultValue={formData[`pesticideType.${index}.unit`] || ''}
                  fieldName={`pesticideType.${index}.unit`}
                  options={pesticideUnitOptions()}
                  required={true}
                  autoFocus={false}
                  fullWidth
                  onChange={onFieldChange}
                  activityId="act19"
                />
              </Box>
            </Box>
          );
        })}
        {selectedPesticideTypes.length === 0 && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Pesticide Amount - Shown when no pesticide types are selected */}
              <MuiTextField
                defaultLabel={intl.formatMessage({ id: 'AMOUNT_OF_PESTICIDE' })}
                defaultValue={formData.pesticideType?.default?.amount || ''}
                fieldName="pesticideType.0.amount"
                numberKeypad={true}
                onChange={handleNumericValidation}
                autoFocus={false}
                required={true}
                errorHelperText={formErrors['pesticideType.0.amount']}
                fullWidth
                activityId="act19"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {/* Pesticide Unit - Shown when no pesticide types are selected */}
              <MuiSelectMobileDropdown
                defaultLabel={`${intl.formatMessage({ id: 'CT_UNIT' })}*`}
                defaultValue={formData.pesticideType?.default?.unit || ''}
                fieldName="pesticideType.0.unit"
                options={pesticideUnitOptions()}
                required={true}
                autoFocus={false}
                fullWidth
                onChange={onFieldChange}
                activityId="act19"
              />
            </Box>
          </>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Pest including subtypes - what attribute ?*/}
        <BottomSheetNestedDropdown
          label={`${intl.formatMessage({ id: 'PEST_SUBTYPE' })}*`}
          bottomSheetTitleText={intl.formatMessage({ id: 'PEST_DATA_LABEL' })}
          options={pesticideDropdowns}
          defaultValue={pestDataValue}
          onChange={(value) => onFieldChange('pestInfo', value)}
          required={true}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '32px' }}>
        {/* New field - 28 distanceOfSurfaceWaterExists */}
        <FormControlLabel
          control={
            <Checkbox
              checked={distanceChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                handleDistanceChange(e);
                onFieldChange('distanceOfSurfaceWaterExists', checked);
              }}
              sx={{
                paddingRight: '5px',
                color: distanceChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={intl.formatMessage({ id: 'DISTANCE_REQUIREMENTS_SURFACE_WATER' })}
          sx={{
            ...taskTemplateStyle.modalDialogSubTitle,
            '& .MuiFormControlLabel-label': {
              textAlign: 'left',
              '& > span': { // Target the element wrapping the text
                display: 'inline-block', // Allows margin manipulation
                marginLeft: '-0.5em', // Adjust this negative value to shift left
              },
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '24px' }}>
        {/* New field - 30 distanceOfSurfaceWater */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'DISTANCE_REQUIREMENTS' })}
          defaultValue={formData.waterJournal.distanceOfSurfaceWater ?? ''}
          fieldName="distanceOfSurfaceWater"
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          error={!!formErrors['distanceOfSurfaceWater']}
          helperText={formErrors['distanceOfSurfaceWater']}
          sx={taskTemplateStyle.checkboxSelectedAttributesStyle}
          required={distanceChecked ? true : false}
          disabled={!distanceChecked} // Disable based on distanceChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {/* vegitatedBufferZone */}
        <FormControlLabel
          control={
            <Checkbox
              checked={vegetationChecked}
              onChange={(e) => {
                const checked = e.target.checked;
                handleVegetationChange(e)
                onFieldChange('vegitatedBufferZone', checked);
              }}
              sx={{
                paddingRight: '5px',
                color: vegetationChecked ? selectedColor : null,
                '&.Mui-checked': {
                  color: selectedColor,
                },
              }}
            />
          }
          label={intl.formatMessage({ id: 'VEGETATED_BUFFER_ZONE_REQUIREMENTS' })}
          sx={{
            ...taskTemplateStyle.modalDialogSubTitle,
            '& .MuiFormControlLabel-label': {
              textAlign: 'left',
              '& > span': { // Target the element wrapping the text
                display: 'inline-block', // Allows margin manipulation
                marginLeft: '-0.5em', // Adjust this negative value to shift left
              },
            },
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', paddingTop: '24px' }}>
        {/* New field - 31 distanceOfVegitagedBufferZone */}
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'BUFFER_ZONE_REQUIREMENTS' })}
          defaultValue={formData.waterJournal.distanceOfVegitagedBufferZone ?? ''}
          fieldName="distanceOfVegitagedBufferZone"
          onChange={handleNumericValidation}
          autoFocus={false}
          numberKeypad={true}
          inputAdornment={intl.formatMessage({ id: 'WATER_JOURNAL_UNIT_M' })}
          error={!!formErrors['distanceOfVegitagedBufferZone']}
          helperText={formErrors['distanceOfVegitagedBufferZone']}
          sx={taskTemplateStyle.checkboxSelectedAttributesStyle}
          required={vegetationChecked ? true : false}
          disabled={!vegetationChecked} // Disable based on vegetationChecked
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <Typography style={taskTemplateStyle.textHeadingStyle}>
          {intl.formatMessage({ id: 'AF_OTHER' })}
        </Typography>
      </Box>
      {/* Reason for pesticide application */}
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '24px' }}>
        <MuiTextField
          defaultLabel={intl.formatMessage({ id: 'RFPA' })}
          defaultValue={formData.reason}
          fieldName="reason"
          onChange={onFieldChange}
          required={true}
          error={!!formErrors['reason']}
          helperText={formErrors['reason']}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {/* Effect of application */}
        <MuiSelectMobileDropdown
          defaultLabel={`${intl.formatMessage({ id: 'EFFECT' })}*`}
          defaultValue={formData.effectApplication}
          fieldName='effectApplication'
          options={getEffectOfApplicationValues()}
          autoFocus={false}
          required={true}
          onChange={onFieldChange}
          activityId="act19"
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", paddingTop: '32px' }}>
        <MuiTextFieldArea
          defaultLabel={intl.formatMessage({ id: 'NOTE' })}
          fieldName='note'
          onChange={onFieldChange}
        />
      </Box>
    </Box >
  );
});

export default AddTaskWaterJournalTemplate1;
